import {
  Alert,
  EAlertVariant,
  EHeadingStyleVariant,
  ELinkVariant,
  EToastVariant,
  Heading,
  Link as InlineLink,
  Text,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToast } from '@/hooks/useToast';
import { getResendUserCredentials } from '@/services/auth/resetPassword';
import { IFullUserInfo } from '@/services/types/user/user';

import { EPersonalInfoFields, TPersonalInfoValues } from '../ProfileEdit';

interface IEmailAddressProps {
  userData: IFullUserInfo;
  control: Control<TPersonalInfoValues>;
  errors: FieldErrors<TPersonalInfoValues>;
}

export const EmailAddress = ({ userData, control, errors }: IEmailAddressProps) => {
  const intl = useIntl();
  const { showToast } = useToast();

  const [emailResend, setEmailResend] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);

  const sendLink = async () => {
    try {
      setIsSendingEmail(true);
      await getResendUserCredentials();

      setEmailResend(true);
      setIsSendingEmail(false);
    } catch {
      setIsSendingEmail(false);
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          defaultMessage: 'Oops, something went wrong. Please, try again later.',
          id: 'uSQB3M',
        }),
      );
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Heading variant={EHeadingStyleVariant.H6}>
        <FormattedMessage defaultMessage="Email" id="sy+pv5" />
      </Heading>
      <Text className="mb-4 text-secondary-base">
        <FormattedMessage defaultMessage="Always keep this email accessible." id="McsoT1" />
      </Text>
      <Controller
        name={EPersonalInfoFields.Email}
        control={control}
        rules={{
          required: intl.formatMessage({
            defaultMessage: 'Email is required',
            id: 'cU/aSG',
          }),
          pattern: {
            value: /^\S+@\S+\.\S+$/,
            message: intl.formatMessage({
              defaultMessage: 'Please enter a valid email.',
              id: 'ui+TC7',
            }),
          },
        }}
        render={({ field: { value, onChange } }) => (
          <TextInput
            controlled
            inputValue={String(value) || ''}
            onChange={onChange}
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Email input field',
              id: 'UzWiq2',
            })}
            label={intl.formatMessage({
              defaultMessage: 'Email',
              id: 'sy+pv5',
            })}
            error={!!errors.email}
            helpText={errors.email?.message}
          />
        )}
      />
      {(userData.pending_email || !userData.verifications.email) &&
        (userData.pending_email ? (
          <Alert
            title={intl.formatMessage({
              defaultMessage: 'Email address verification pending',
              id: '8FvlkJ',
            })}
            content={intl.formatMessage(
              {
                defaultMessage: `The email address ({newEmail}) change is pending on your account. Your email address will be updated once you've verified your new email address. {question} {resendLink}`,
                id: '7j+8on',
              },
              {
                newEmail: userData.pending_email,
                question: !emailResend ? (
                  <FormattedMessage defaultMessage="Didn't receive an email?" id="AzKGnN" />
                ) : undefined,
                resendLink: !emailResend ? (
                  <InlineLink
                    variant={ELinkVariant.Underline}
                    onClick={sendLink}
                    disabled={isSendingEmail}>
                    <FormattedMessage defaultMessage="Resend email." id="GeU8YM" />
                  </InlineLink>
                ) : undefined,
              },
            )}
            variant={EAlertVariant.Alert}
            className="mt-4"
          />
        ) : (
          <Alert
            title={intl.formatMessage({
              defaultMessage: 'Email address unverified',
              id: 'gW35g0',
            })}
            content={intl.formatMessage(
              {
                defaultMessage: `Please check your email inbox and follow the link in the email we've sent. {question} {resendLink}`,
                id: 'RUmV0o',
              },
              {
                question: !emailResend ? (
                  <FormattedMessage defaultMessage="Didn’t receive email?" id="zmRCQK" />
                ) : undefined,
                resendLink: !emailResend ? (
                  <InlineLink
                    variant={ELinkVariant.Underline}
                    onClick={sendLink}
                    disabled={isSendingEmail}>
                    <FormattedMessage defaultMessage="Resend email." id="GeU8YM" />
                  </InlineLink>
                ) : undefined,
              },
            )}
            variant={EAlertVariant.Error}
            className="mt-4"
          />
        ))}
    </div>
  );
};
